import type { CopyOf } from "@ifgengineering/copy-catalogue";

export type HomeCopy = CopyOf<"home">;

import {
  CurrencyEnumType,
  Deal,
  Label,
} from "@ifgengineering/client-invest-sdk";
import { filterLabelsForLiveDeals } from "@components/deals/helpers";
import { formatCurrencyWhole, formatShortDate } from "./format";

export const TARGET_RETURN_LABEL_NAMES = [
  "Annual Target Yield",
  "Annual Target Return",
  "Annualised Target Return",
] as const;

export const RISK_LABEL_NAMES = ["Risk Profile", "Risk"] as const;

export const DISTRIBUTION_LABEL_NAMES = [
  "Distribution Paid",
  "Distributions Paid",
] as const;

export const LIQUIDITY_LABEL_NAMES = ["Liquidity"] as const;

export const TERM_LABEL_NAMES = ["Liquidity", "Maturity"] as const;

/** Values a fund card overrides with its own tenor-derived headline. */
export const PRIMARY_LABEL_NAMES: readonly string[] = [
  ...TARGET_RETURN_LABEL_NAMES,
  ...LIQUIDITY_LABEL_NAMES,
];

const normalise = (value: string): string => value.trim().toLowerCase();

export const matchesLabelName = (
  labelName: string | undefined,
  names: readonly string[]
): boolean =>
  !!labelName && names.some((name) => normalise(name) === normalise(labelName));

/**
 * Resolves a deal stat by label name, first match in priority order. A label
 * only counts when it has a value and no bgColor, mirroring DealCardV2.
 */
export const findVisibleDealLabelValue = (
  deal: Deal,
  names: readonly string[]
): string | undefined => {
  for (const name of names) {
    const target = normalise(name);
    const match = deal.labels?.find(
      (label) =>
        label.name &&
        normalise(label.name) === target &&
        !label.bgColor &&
        label.value
    );
    const value = match?.value?.trim();
    if (value) return value;
  }
  return undefined;
};

/** Reported as `deal_type`; the same two values the mobile list reports. */
export type LiveDealType = "income" | "growth";

const GROWTH_ASSET_CLASSES = ["venture capital", "private equity"];

/** Maps a deal to income/growth - investable.type first, asset class as fallback. */
export const classifyDealType = (deal: Deal): LiveDealType => {
  const type = deal.investable?.type;
  if (type === "SHARE") return "growth";
  if (type === "INCOME") return "income";
  const assetClass = deal.investable?.metadata?.assetClass?.toLowerCase() ?? "";
  return GROWTH_ASSET_CLASSES.some((growth) => assetClass.includes(growth))
    ? "growth"
    : "income";
};

export interface LiveDealCard {
  id: string;
  name: string;
  currency: CurrencyEnumType;
  assetClass: string;
  dealType: LiveDealType;
  icon?: string;
  investableType?: string;
  isaEligible: boolean;
  imageUrl?: string;
  yieldLabel?: string;
  risk?: string;
  distribution?: string;
  term?: string;
  minInvest?: string;
  statusText: string;
  isLive: boolean;
  labels: Label[];
  deal: Deal;
}

const formatMinInvest = (
  amount: number | null | undefined,
  currency: CurrencyEnumType
): string | undefined => {
  if (amount == null || amount <= 0) return undefined;
  return formatCurrencyWhole(amount, currency);
};

const buildStatusText = (
  deal: Deal,
  isLive: boolean,
  copy: HomeCopy
): string => {
  const closes = formatShortDate(deal.expiryDate);
  if (isLive) {
    return closes
      ? copy.liveDeals.windowOpenClosing({ closes })
      : copy.liveDeals.windowOpen;
  }
  const opens = formatShortDate(deal.launchDate);
  if (!opens) return copy.liveDeals.windowComingSoon;
  return closes
    ? copy.liveDeals.windowOpensCloses({ opens, closes })
    : copy.liveDeals.windowOpens({ opens });
};

export const dealImageUrl = (deal: Deal): string | undefined =>
  deal.dealImage?.url ||
  deal.investable?.backgroundImage?.url ||
  deal.investable?.displayImage?.url ||
  undefined;

export const buildLiveDealCard = (deal: Deal, copy: HomeCopy): LiveDealCard => {
  const currency = deal.investable?.currency ?? "GBP";
  const isLive = deal.status !== "PRELAUNCH";

  return {
    id: deal.id,
    name: deal.name,
    currency,
    assetClass:
      deal.investable?.metadata?.assetClass ||
      copy.liveDeals.dealTypeInvestment,
    dealType: classifyDealType(deal),
    icon: deal.investable?.icon ?? undefined,
    investableType: deal.investable?.type ?? undefined,
    isaEligible: deal.isIFISADeal === true,
    imageUrl: dealImageUrl(deal),
    yieldLabel: findVisibleDealLabelValue(deal, TARGET_RETURN_LABEL_NAMES),
    risk: findVisibleDealLabelValue(deal, RISK_LABEL_NAMES),
    distribution: findVisibleDealLabelValue(deal, DISTRIBUTION_LABEL_NAMES),
    // Left as authored: only a fund group abbreviates, and it builds "3M · 6M"
    // from each tenor's notice months rather than shortening a typed label.
    term: findVisibleDealLabelValue(deal, TERM_LABEL_NAMES),
    minInvest: formatMinInvest(deal.minimumInvestment, currency),
    statusText: buildStatusText(deal, isLive, copy),
    isLive,
    labels: filterLabelsForLiveDeals(deal.labels ?? []).filter(
      (label) => label.value
    ),
    deal,
  };
};
